// Format currency values
export const formatCurrency = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return '$0.00';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '$0.00';
  return usdFormatter.format(numValue);
};

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/**
 * Rounds a currency amount to 2 decimal places.
 *
 * Use this to collapse sub-cent floating-point drift (e.g., `2.5 * 39.99 =
 * 99.97499999999999`) before displaying, storing, or comparing monetary
 * values. Without this, the value shown to the user and the value used
 * downstream can disagree by a fraction of a cent (PRA-732).
 *
 * The `Number.EPSILON` bump nudges values that are mathematically equal to
 * a half-cent but stored slightly below (e.g., `0.005` as `0.00499...`)
 * past the rounding boundary, matching the "round half away from zero"
 * behavior that `Intl.NumberFormat` uses for display. We round the absolute
 * value and then re-apply the sign so negatives round symmetrically — adding
 * `EPSILON` directly to a negative would nudge it toward zero, not away.
 */
export const roundCurrencyAmount = (value: number): number => {
  const roundedAbsoluteValue =
    Math.round((Math.abs(value) + Number.EPSILON) * 100) / 100;
  // Avoid returning `-0` for tiny negative inputs like `-0.001` (which round
  // to zero magnitude). `-0` compares equal to `0` but trips up code that
  // inspects sign downstream.
  if (roundedAbsoluteValue === 0) {
    return 0;
  }
  return Math.sign(value) * roundedAbsoluteValue;
};

// Coerces a string/number into a finite number, falling back to `0` for
// missing values, NaN, or non-numeric strings. Useful when summing fields
// from documents whose numeric values may arrive as strings.
export const validNumber = (value: string | number | undefined): number => {
  if (typeof value === 'number') {
    if (isNaN(value)) {
      return 0;
    }
    return value;
  }
  if (typeof value === 'string') {
    const num = Number(value);
    if (isNaN(num)) {
      return 0;
    }
    return num;
  }
  return 0;
};
